import ENV from "@/utils/env";
import { api } from "@/lib/api";
import type {
  RendererBaseParams,
  HeatmapParams,
  AttentionVisibilityParams,
  AttentionClarityParams,
  AttentionDigestibilityParams,
  MemoryParams,
  EmotionParams,
  Visualisation,
  VideoRender,
  VideoSceneFrameParams,
} from "./types/renderer.type";

/*
 * Renderer API Functions
 * These functions fetch the generated images from S3 via our pipeline.
 * They accept parameters to filter and modify the images as needed depending on the report type/ KPI.
 *
 * We mainly use these to render images straight into img tags by setting the src attribute to the returned URL rather
 * than a traditional api call.
 */

/* Constants */
const BASE_URL = `${ENV.API_PLATFORM_URL}/pipeline/renderer/images/v1`;
const VIDEO_BASE_URL = `${ENV.API_PLATFORM_URL}/pipeline/renderer/videos/v1`;
const DEFAULT_WIDTH = 1024;
const DEFAULT_HEIGHT = 1024;

/* Helper function to build URL with query params */
const buildUrl = (endpoint: string, baseParams: RendererBaseParams, additionalParams: Record<string, string | boolean> = {}): string => {
  const url = new URL(`${BASE_URL}${endpoint}`);

  // Add required base params
  url.searchParams.set("prefix", `${baseParams.projectId}/${baseParams.studyId}`);
  url.searchParams.set("key", baseParams.assetKey);
  url.searchParams.set("maxWidth", DEFAULT_WIDTH.toString());
  url.searchParams.set("maxHeight", DEFAULT_HEIGHT.toString());

  // Add additional params
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, value.toString());
    }
  });

  return url.toString();
};

/**
 * Get the original image
 */
export const getOriginalImageUrl = (params: RendererBaseParams): string => {
  return buildUrl("", params);
};

/**
 * Get heatmap image
 */
export const getHeatmapUrl = ({ heatmapType, ...baseParams }: HeatmapParams, visualisation?: Visualisation): string => {
  const params: Record<string, string> = {};

  // Add visual param if needed
  if (visualisation && visualisation !== "none") {
    params.visual = visualisation;
  } else if (heatmapType === "opacity") {
    params.visual = "opacity";
  }

  return buildUrl("/attention/heatmaps", baseParams, params);
};

/**
 * Get the asset with a context renderered with a visibility score
 */
export const getAttentionVisibilityUrl = ({ contextKey, ...baseParams }: AttentionVisibilityParams, visualisation?: Visualisation): string => {
  const params: Record<string, string | boolean> = {
    contextKey,
    metric: "vis",
    ranked: false,
  };

  if (visualisation) {
    params.visual = visualisation;
  }

  return buildUrl("/attention/contexts", baseParams, params);
};

/**
 * Get the asset with the option of hotspots and gaze path
 */
export const getAttentionClarityUrl = ({ subMetric, showHotspots, showGazePath, ...baseParams }: AttentionClarityParams, visualisation?: Visualisation): string => {
  const params: Record<string, string | boolean> = {
    metric: subMetric === "pa" ? "pop" : subMetric,
    ranked: showGazePath,
    hotspots: showHotspots,
  };

  if (visualisation) {
    params.visual = visualisation;
  }

  return buildUrl("/attention/hotspots", baseParams, params);
};

/**
 * Get the asset with AOI regions rendered
 */
export const getAttentionDigestibilityUrl = ({ subMetric, showAOIRank, ...baseParams }: AttentionDigestibilityParams, visualisation?: Visualisation): string => {
  const params: Record<string, string | boolean> = {
    metric: subMetric === "pa" ? "pop" : subMetric,
    ranked: showAOIRank,
  };

  if (visualisation) {
    params.visual = visualisation;
  }

  return buildUrl("/attention/regions", baseParams, params);
};

/**
 * Get the asset with memory regions rendered based on their memory class
 */
export const getMemoryUrl = ({ metric, ...baseParams }: MemoryParams): string => {
  return buildUrl("/memory/regions", baseParams, {
    memoryClass: metric,
  });
};

/**
 * Get the asset with emotion regions rendered based on their emotion class
 */
export const getEmotionUrl = ({ metric, ...baseParams }: EmotionParams): string => {
  return buildUrl("/emotion/regions", baseParams, {
    emotionClass: metric,
  });
};

/**
 * Get the generated thumbnail for a video asset
 */
export const getVideoThumbnailUrl = (params: RendererBaseParams): string => {
  const url = new URL(`${VIDEO_BASE_URL}/thumbnail`);
  url.searchParams.set("prefix", `${params.projectId}/${params.studyId}`);
  url.searchParams.set("key", params.assetKey);
  return url.toString();
};

/**
 * Get a single extracted scene frame for a video asset. The frameName is the relative path
 * returned in a video insight's scenes[].frames list (e.g. "scene_001/frame_001.jpg").
 */
export const getVideoSceneFrameUrl = ({ frameName, ...baseParams }: VideoSceneFrameParams): string => {
  const url = new URL(`${VIDEO_BASE_URL}/scene`);
  url.searchParams.set("prefix", `${baseParams.projectId}/${baseParams.studyId}`);
  url.searchParams.set("key", baseParams.assetKey);
  url.searchParams.set("frameName", frameName);
  return url.toString();
};

/**
 * Get a short-lived pre-signed S3 URL for a video's original render, so it can be streamed
 * directly by a video player. Unlike the other renderer functions above, this hits a JSON
 * endpoint rather than returning a URL synchronously, since the URL itself must be signed
 * server-side and expires after an hour.
 */
export const getVideoOriginalRender = async (params: RendererBaseParams): Promise<VideoRender> => {
  const resp = await api.get(VIDEO_BASE_URL, {
    params: {
      prefix: `${params.projectId}/${params.studyId}`,
      key: params.assetKey,
    },
  });
  return resp.data.data as VideoRender;
};

/**
 * Get a short-lived pre-signed S3 URL for a video's attention heatmap-overlay render, for the
 * player's original/heatmap toggle. Same response shape and expiry behaviour as
 * getVideoOriginalRender above, just a different render variant.
 */
export const getVideoHeatmapRender = async (params: RendererBaseParams): Promise<VideoRender> => {
  const resp = await api.get(`${VIDEO_BASE_URL}/attention/heatmaps`, {
    params: {
      prefix: `${params.projectId}/${params.studyId}`,
      key: params.assetKey,
    },
  });
  return resp.data.data as VideoRender;
};
